import { useEffect, useRef } from 'react';

import type { Location } from 'history';
import { useHistory } from 'react-router-dom';

interface UseUnsavedChangesGuardOptions {
  hasUnsavedChanges: boolean;
  onNavigationAttempt: (location: Location) => void;
}

export interface UnsavedChangesGuard {
  releaseAndNavigate: (location: Location) => void;
}

export function useUnsavedChangesGuard({
  hasUnsavedChanges,
  onNavigationAttempt,
}: UseUnsavedChangesGuardOptions): UnsavedChangesGuard {
  const history = useHistory();
  const unblockRef = useRef<(() => void) | null>(null);

  useEffect(() => {
    if (!hasUnsavedChanges) {
      unblockRef.current?.();
      unblockRef.current = null;
      return;
    }

    unblockRef.current = history.block((location) => {
      onNavigationAttempt(location);
      return false;
    });

    return () => {
      unblockRef.current?.();
      unblockRef.current = null;
    };
  }, [hasUnsavedChanges, history, onNavigationAttempt]);

  useEffect(() => {
    if (!hasUnsavedChanges) {
      return;
    }

    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = '';
      return '';
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [hasUnsavedChanges]);

  return {
    releaseAndNavigate: (location: Location) => {
      unblockRef.current?.();
      unblockRef.current = null;
      history.push(location.pathname + location.search + location.hash);
    },
  };
}
